import * as React from 'react';
import { useMemo } from 'react';
import { Stack, Typography, Box } from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { cores } from '../styles/styleUtils';
import { prDaProposta } from '../utils/performanceRatio';

// ── Constantes ────────────────────────────────────────────────────────
const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const HSP_KEYS = ['hspJan','hspFev','hspMar','hspAbr','hspMai','hspJun',
                  'hspJul','hspAgo','hspSet','hspOut','hspNov','hspDez'];

const COR_PRODUCAO = '#1565C0';
const COR_ECONOMIA = '#F57C00';
const COR_ANUAL    = '#2E7D32';

// ── Rótulo centralizado nas barras ───────────────────────────────────
const RoruloBarras = ({ x, y, width, height, value, cor, fontSize = 10 }) => {
  if (!value || height < 14) return null;
  return (
    <text
      x={x + width / 2}
      y={y + height / 2 + fontSize / 3}
      fill="#fff"
      textAnchor="middle"
      fontSize={fontSize}
      fontWeight="bold"
    >
      {typeof value === 'number' ? value.toFixed(0) : value}
    </text>
  );
};

// ── Componente principal ──────────────────────────────────────────────
export default function PreviewProducao({ proposta }) {
  const [hsp, setHsp] = React.useState(proposta.hsp || null);

  // Buscar HSP se não estiver salvo na proposta (ex: visualização pelo fluxo de vendas)
  React.useEffect(() => {
    if (!proposta.hsp && proposta.localinstalacao?.cidade?.id) {
      import('./api').then(({ getHspIbge, getHsp }) => {
        const ibge = Number(proposta.localinstalacao.cidade.id);
        const cidade = proposta.localinstalacao.cidade.nome;
        const uf = proposta.localinstalacao.estado?.sigla;
        getHspIbge(ibge)
          .then((h) => {
            if (h) { setHsp(h); return null; }
            return getHsp(cidade, uf);
          })
          .then((h) => { if (h) setHsp(h); })
          .catch(console.warn);
      });
    }
  }, [proposta.localinstalacao?.cidade?.id]);

  const pr = prDaProposta(proposta);

  // Fator de orientação para exibição da geração real no Preview
  const fatorOrientacao = (() => {
    const id = proposta.orientacaotelhado?._id?.toString();
    if (id === '6508a00a326edddd6ff36617') return 0.85; // Leste/Oeste
    if (id === '6508a00a326edddd6ff36618' && !proposta.rebatimento) return 0.70; // Sul sem rebatimento
    return 1.0; // Norte ou Sul com rebatimento
  })();

  const potCC = proposta.kit?.potenciacc || 0;
  const tarifaEnergia  = proposta.tarifaEnergia
    ? parseFloat(proposta.tarifaEnergia)
    : 0.99;

  // Depreciação do módulo
  const deprecPrimeiro = parseFloat(proposta.kit?.modulo?.depreciacaoprimeiro || 2.0) / 100;
  const deprecDemais   = parseFloat(proposta.kit?.modulo?.depreciacaodemais   || 0.7)  / 100;

  // ── Gráfico 1 — Produção e Economia Mensal ───────────────────────
  const dadosMensais = useMemo(() => {
    if (!hsp || !potCC) return [];
    return MESES.map((mes, i) => {
      const hspMes  = hsp[HSP_KEYS[i]] || 0;
      const energia = parseFloat((potCC * hspMes * 30 * pr * fatorOrientacao).toFixed(1));
      const economia = parseFloat((energia * tarifaEnergia).toFixed(2));
      return { mes, 'Produção (kWh)': energia, 'Economia (R$)': economia };
    });
  }, [hsp, potCC, pr, fatorOrientacao, tarifaEnergia]);

  // ── Gráfico 2 — Produção Anual 25 anos ──────────────────────────
  const dadosAnuais = useMemo(() => {
    if (!hsp || !potCC) return [];
    const hspMedia = hsp.hspMedia || 0;
    const ano0 = potCC * hspMedia * 30 * pr * fatorOrientacao * 12;

    return Array.from({ length: 26 }, (_, i) => {
      const ano = new Date().getFullYear() + i;
      let producao;
      if (i === 0) {
        producao = ano0;
      } else if (i === 1) {
        producao = ano0 * (1 - deprecPrimeiro);
      } else {
        // acumula depreciação anual
        producao = ano0 * (1 - deprecPrimeiro) * ((1 - deprecDemais) ** (i - 1));
      }
      return { ano: String(ano), 'Produção (kWh)': parseFloat(producao.toFixed(0)) };
    });
  }, [hsp, potCC, pr, fatorOrientacao, deprecPrimeiro, deprecDemais]);

  // ── Totais para exibir ───────────────────────────────────────────
  const totalAnual   = dadosMensais.reduce((s, d) => s + d['Produção (kWh)'], 0).toFixed(0);
  const totalEconomia = dadosMensais.reduce((s, d) => s + d['Economia (R$)'], 0).toFixed(2);
  const producao25   = dadosAnuais.reduce((s, d) => s + d['Produção (kWh)'], 0).toFixed(0);

  if (!hsp || !potCC) {
    return (
      <Stack alignItems="center" justifyContent="center" flex={1}>
        <Typography color="error">
          Dados insuficientes para gerar o preview. Verifique o kit e a cidade de instalação.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack flex={1} gap={3} px={2} py={1} sx={{ overflowY: 'auto' }}>

      {/* ── Resumo ── */}
      <Stack direction="row" gap={3} justifyContent="center" flexWrap="wrap">
        {[
          { label: 'Potência CC', valor: `${potCC} kWp` },
          { label: 'HSP Médio',   valor: `${hsp.hspMedia?.toFixed(2)} h/dia` },
          { label: 'PR',          valor: `${(pr * 100).toFixed(0)}%` },
          { label: 'Produção Anual', valor: `${Number(totalAnual).toLocaleString('pt-BR')} kWh` },
          { label: 'Economia Anual', valor: parseFloat(totalEconomia).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
        ].map((item) => (
          <Box
            key={item.label}
            sx={{
              backgroundColor: '#E3F2FD',
              border: '1px solid #90CAF9',
              borderRadius: 2,
              px: 3, py: 1.5,
              textAlign: 'center',
              minWidth: 130,
            }}
          >
            <Typography variant="caption" color="text.secondary">{item.label}</Typography>
            <Typography variant="subtitle1" fontWeight="bold" color="#1565C0">{item.valor}</Typography>
          </Box>
        ))}
      </Stack>

      {/* ── Gráfico 1 — Mensal ── */}
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" color="#1565C0" mb={1} textAlign="center">
          Produção Mensal × Economia Mensal
        </Typography>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={dadosMensais} margin={{ top: 16, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
            <XAxis dataKey="mes" tick={{ fontSize: 12, fontWeight: 'bold', fill: '#1565C0' }} />
            <YAxis tick={{ fontSize: 11, fill: cores.textoClaro }} />
            <Tooltip formatter={(v, name) =>
              name === 'Economia (R$)'
                ? [v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), name]
                : [`${v} kWh`, name]
            } />
            <Legend wrapperStyle={{ color: cores.textoClaro }} />
            <Bar dataKey="Produção (kWh)" fill={COR_PRODUCAO} radius={[3,3,0,0]}>
              {dadosMensais.map((_, i) => (
                <RoruloBarras key={i} cor={COR_PRODUCAO} />
              ))}
            </Bar>
            <Bar dataKey="Economia (R$)" fill={COR_ECONOMIA} radius={[3,3,0,0]}>
              {dadosMensais.map((_, i) => (
                <RoruloBarras key={i} cor={COR_ECONOMIA} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* ── Gráfico 2 — Anual 25 anos ── */}
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" color="#1565C0" mb={1} textAlign="center">
          Produção Anual — 25 anos &nbsp;
          <Typography component="span" variant="caption" sx={{ color: cores.textoClaro, opacity: 0.7 }}>
            (Total: {Number(producao25).toLocaleString('pt-BR')} kWh)
          </Typography>
        </Typography>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={dadosAnuais} margin={{ top: 16, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
            <XAxis dataKey="ano" tick={{ fontSize: 10, fontWeight: 'bold', fill: '#2E7D32' }} angle={-45} textAnchor="end" height={48} />
            <YAxis tick={{ fontSize: 11, fill: cores.textoClaro }} />
            <Tooltip formatter={(v) => [`${Number(v).toLocaleString('pt-BR')} kWh`, 'Produção']} />
            <Bar dataKey="Produção (kWh)" fill={COR_ANUAL} radius={[3,3,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Typography variant="caption" textAlign="center" sx={{ color: cores.textoClaro, opacity: 0.7 }}>
        * Estimativa baseada em dados solarimétricos PVGIS. Valores reais podem variar conforme sombreamento,
        temperatura e condições locais. Depreciação: {(deprecPrimeiro * 100).toFixed(1)}% no 1º ano,
        {(deprecDemais * 100).toFixed(1)}% nos demais.
      </Typography>

    </Stack>
  );
}
